import React, { useRef, useState, useEffect } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { Button, Input, message, Space, Table } from 'antd';
import { Link } from "react-router-dom";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useSelector } from 'react-redux';
import InputMask from 'react-input-mask';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { fetchProductGetAll, deleteProductById } from '../../view/product/action/action';
import { GlobalConfirmModal, GlobalSuccessModal } from '../modalGlobal/modalGlobal';

const DataProductTable = () => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [datas, setData] = useState([]);
    const [loading, setLoading] = useState(true); 
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const searchInput = useRef(null);

    const user = useSelector(state => state.user);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const fetchData = async () => {
        setLoading(true);

        try {
            const result = await fetchProductGetAll(user);

            if (result.success) {
                setData(result.data);
            } else {
                console.error("Erro ao buscar dados dos produtos:", result.error);
            }
        } catch (error) {
            console.error("Erro ao buscar dados dos produtos:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchData();
        }
    }, [user]);

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const handleDeleteClick = (id) => {
        setProductToDelete(id);
        setOpenConfirmModal(true);
    };

    const handleConfirmDelete = async () => {
        setOpenConfirmModal(false);
        if (!productToDelete) return;
        try {
            const result = await deleteProductById(productToDelete);

            if (!result.success) {
                throw new Error(result.error || "Erro ao excluir o produto");
            }

            setOpenSuccessModal(true);
            fetchData();
        }
        catch (error) {
            message.error("Erro ao excluir o produto. Tente novamente.");
            console.error(error);
        }
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div
            style={{
            padding: 8,
            }}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{
                marginBottom: 8,
                display: 'block',
            }}
            />
            <Space>
            <Button
                type="primary"
                onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                icon={<SearchOutlined />}
                size="small"
                style={{
                width: 90,
                }}
            >
                Search
            </Button>
            <Button
                onClick={() => clearFilters && handleReset(clearFilters)}
                size="small"
                style={{
                width: 90,
                }}
            >
                Reset
            </Button>
            <Button
                type="link"
                size="small"
                onClick={() => {
                confirm({
                    closeDropdown: false,
                });
                setSearchText(selectedKeys[0]);
                setSearchedColumn(dataIndex);
                }}
            >
                Filter
            </Button>
            <Button
                type="link"
                size="small"
                onClick={() => {
                close();
                }}
            >
                close
            </Button>
            </Space>
        </div>
        ),
        
        filterIcon: (filtered) => (
        <SearchOutlined
            style={{
            color: filtered ? '#1677ff' : undefined,
            }}
        />
        ),

        onFilter: (value, record) =>{ 

        if (dataIndex === 'cpfCnpj') {
            const cleanValue = value.replace(/[^\w]/g, '').trim();
        
            return (
            (record.cpf && record.cpf.toString().replace(/[^\w]/g, '').toLowerCase().includes(cleanValue.toLowerCase())) ||
            (record.cnpj && record.cnpj.toString().replace(/[^\w]/g, '').toLowerCase().includes(cleanValue.toLowerCase()))
            );
        }
        else if(typeof value === 'string'){
            return record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase())
        }
        else {
            return record[dataIndex] = value
        }

        }, onFilterDropdownOpenChange: (visible) => {
        if (visible) {
            setTimeout(() => searchInput.current?.select(), 100);
        }
        },

        render: (text) =>
        searchedColumn === dataIndex ? (
            <Highlighter
            highlightStyle={{
                backgroundColor: '#ffc069',
                padding: 0,
            }}
            searchWords={[searchText]}loading={loading}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
            />
        ) : (
            text
        ),
    });

    const columns = [
        {
            title: 'Produto',
            dataIndex: 'product',
            key: 'product',
            ...getColumnSearchProps('product'),
            sorter: (a, b) => a.product.localeCompare(b.product),
        },
        {
            title: 'Descrição',
            dataIndex: 'description',
            key: 'description',
            ...getColumnSearchProps('description'),
            sorter: (a, b) => {
                const nameA = a.description || '';
                const nameB = b.description || '';
                return nameA.localeCompare(nameB);
            },
        },
        {
            title: 'Ativo',
            dataIndex: 'activo',
            key: 'activo',
            ...getColumnSearchProps('activo'),
            sorter: (a, b) => {
                const nameA = a.active || '';
                const nameB = b.active || '';
                return nameA.localeCompare(nameB);
            },
            render: (activo) => (
                <span>{activo ? 'Sim' : 'Não'}</span>
            ),
        },
        {
        title: 'Ações',
        key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    {
                        user?.permissoes?.product?.isEdit || user?.permissoes?.produto?.editar ? (
                            <Link to={'/product/' + record.id}>
                                <EditOutlinedIcon />
                            </Link>
                    ) : (
                        <Link to={`/product/${record.id}`} state={{viewOnly: true}}>
                            <VisibilityIcon />
                        </Link>
                    )
                    }
                    {
                        user?.permissoes?.product?.isDelete || user?.permissoes?.produto?.excluir ? (
                            <Link to={'#'}>
                                <DeleteOutlinedIcon onClick={(e) => { e.preventDefault(); handleDeleteClick(record.id); }} color='error'/>
                            </Link>
                    ) : (
                        ""
                    )
                    }
                </Space>
            ),
        },
    ];

    return (
        <>
            <Table columns={columns} dataSource={datas} loading={loading} />
            <GlobalConfirmModal
                open={openConfirmModal}
                handleClose={() => setOpenConfirmModal(false)}
                handleConfirm={handleConfirmDelete}
                title="Excluir produto?"
                subtitle="Esta ação não poderá ser desfeita."
            />
            <GlobalSuccessModal
                open={openSuccessModal}
                handleClose={() => setOpenSuccessModal(false)}
                title="Produto excluído com sucesso!"
            />
        </>
    );

};

export default DataProductTable;